import { getLocalStorage } from '@shared/core';

/**
 * Discrete insight returned by the auto-populate mutation for a single accommodation place.
 * We persist this locally so admins can compare successive AI runs without re-calling the API.
 */
export type AutoPopulatedAccommodationInsight = Readonly<{
  accommodationPlaceId: string;
  whyThisHotelIsAGreatPick?: string | null;
  proposedNoteFromHostToGuest?: string | null;
}>;

/**
 * Normalized insight shape stored in localStorage. All values are optional strings to ensure safe serialization.
 */
export type AutoPopulatedAccommodationInsightContent = Readonly<{
  whyThisHotelIsAGreatPick: string | null;
  proposedNoteFromHostToGuest: string | null;
}>;

/**
 * Map of accommodation place id to its stored insight content.
 */
export type AutoPopulatedAccommodationInsightsMap = Readonly<Record<string, AutoPopulatedAccommodationInsightContent>>;

const AUTO_POPULATED_ACCOMMODATION_INSIGHTS_KEY = (eventId: string) => `autoPopulatedAccommodationInsights_${eventId}`;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

const parseStoredInsights = (rawValue: string | null): AutoPopulatedAccommodationInsightsMap => {
  if (!rawValue) {
    return {};
  }

  try {
    const parsed = JSON.parse(rawValue);
    if (!isRecord(parsed)) {
      return {};
    }

    return Object.entries(parsed).reduce<Record<string, AutoPopulatedAccommodationInsightContent>>((acc, [placeId, insight]) => {
      if (!isRecord(insight)) {
        return acc;
      }

      const whyThisHotelIsAGreatPickValue = insight['whyThisHotelIsAGreatPick'];
      const proposedNoteFromHostToGuestValue = insight['proposedNoteFromHostToGuest'];

      const normalizedInsight: AutoPopulatedAccommodationInsightContent = {
        // resolve from type unknown to string
        whyThisHotelIsAGreatPick: typeof whyThisHotelIsAGreatPickValue === 'string' ? whyThisHotelIsAGreatPickValue : null,
        proposedNoteFromHostToGuest: typeof proposedNoteFromHostToGuestValue === 'string' ? proposedNoteFromHostToGuestValue : null,
      };

      acc[placeId] = normalizedInsight;
      return acc;
    }, {});
  } catch {
    return {};
  }
};

/**
 * Returns locally persisted auto-population insights for the given event.
 */
export const getAutoPopulatedAccommodationInsights = (eventId?: string | null): AutoPopulatedAccommodationInsightsMap => {
  if (!eventId) {
    return {};
  }

  const localStorage = getLocalStorage();
  const storedInsights = parseStoredInsights(localStorage.getItem(AUTO_POPULATED_ACCOMMODATION_INSIGHTS_KEY(eventId)) || null);
  return storedInsights;
};

/**
 * Persists the auto-populated insight payload to localStorage so admins can re-surface it later.
 */
export const setAutoPopulatedAccommodationInsights = (eventId: string, insights: ReadonlyArray<AutoPopulatedAccommodationInsight>) => {
  const localStorage = getLocalStorage();
  const existingInsights = parseStoredInsights(localStorage.getItem(AUTO_POPULATED_ACCOMMODATION_INSIGHTS_KEY(eventId)) || null);

  const nextInsights = { ...existingInsights };

  insights.forEach(insight => {
    nextInsights[insight.accommodationPlaceId] = {
      whyThisHotelIsAGreatPick: insight.whyThisHotelIsAGreatPick ?? null,
      proposedNoteFromHostToGuest: insight.proposedNoteFromHostToGuest ?? null,
    };
  });

  localStorage.setItem(AUTO_POPULATED_ACCOMMODATION_INSIGHTS_KEY(eventId), JSON.stringify(nextInsights));
};

/**
 * Clears cached insight data for the event.
 */
export const removeAutoPopulatedAccommodationInsights = (eventId?: string | null) => {
  if (!eventId) {
    return;
  }
  const localStorage = getLocalStorage();
  localStorage.setItem(AUTO_POPULATED_ACCOMMODATION_INSIGHTS_KEY(eventId), null);
};
